import AppMessage from '@/components/AppMessage/message'
import trackEvent from '@/track/trackEvent'
import { throwError } from 'element-plus/es/utils/error.mjs'

interface RequestOptions extends RequestInit {
  baseUrl?: string
  timeout?: number
  errorModalAutoClose?: boolean // 接口发生错误，弹窗是否自动关闭
  isShowErrorModal?: boolean // 接口发生错误，是否弹窗
  retry?: number
  [key: string]: any
}

export interface BaseResponse<T = any> {
  code: number
  data: T
  msg: string
}

interface RequestInterceptor {
  onFulfilled?: (config: RequestOptions) => RequestOptions | Promise<RequestOptions>
  onRejected?: (error: any) => any
}

interface ResponseInterceptor {
  onFulfilled?: (response: BaseResponse) => BaseResponse | Promise<BaseResponse>
  onRejected?: (error: any) => any
}

class HttpError extends Error {
  code: number
  constructor(code: number, message: string) {
    super(message)
    this.name = 'HttpError'
    this.code = code
  }
}

export class HttpClient {
  private baseUrl: string
  private timeout: number
  private requestInterceptors: RequestInterceptor[] = []
  private responseInterceptors: ResponseInterceptor[] = []

  constructor(options?: RequestOptions) {
    this.baseUrl = options?.baseUrl || ''
    this.timeout = options?.timeout || 100000
  }

  requestInterceptor(interceptor: RequestInterceptor) {
    this.requestInterceptors.push(interceptor)
    return this
  }

  responseInterceptor(interceptor: ResponseInterceptor) {
    this.responseInterceptors.push(interceptor)
    return this
  }

  private async processConfig(config: RequestOptions): Promise<RequestOptions> {
    let processedConfig = { ...config }
    for (const interceptor of this.requestInterceptors) {
      if (interceptor.onFulfilled) {
        processedConfig = await interceptor.onFulfilled(config)
      }
    }
    return processedConfig
  }

  private async processResponse(response: BaseResponse): Promise<BaseResponse> {
    let processedResponse = { ...response }
    for (const interceptor of this.responseInterceptors) {
      if (interceptor.onFulfilled) {
        processedResponse = await interceptor.onFulfilled(response)
      }
    }
    return processedResponse
  }

  private async handleError(
    error: HttpError,
    url: string,
    errorModalAutoClose: boolean = true,
    isShowErrorModal: boolean = true
  ) {
    if (error.name === 'AbortError') return
    for (const interceptor of this.responseInterceptors) {
      if (interceptor.onRejected) {
        return interceptor.onRejected(error)
      }
    }

    // token过期或者手动清除缓存后，需要弹登录窗口
    // if (error.code === 1003) {
    //   LoginService({
    //     type: 'login',
    //     onSuccess() {}
    //   })
    // } else {
    //   if (isShowErrorModal) {
    //     const netOffline =
    //       error.message === 'Failed to fetch' || error.message === 'Internal Server Error'
    //     const msg = netOffline ? '网络连接错误，请重试' : error.message || '请求失败'
    //     AppMessage({
    //       type: 'error',
    //       message: msg,
    //       title: '',
    //       timer: errorModalAutoClose ? undefined : 0
    //     })
    //   }
    // }

    if (isShowErrorModal) {
      const netOffline =
        error.message === 'Failed to fetch' || error.message === 'Internal Server Error'
      const msg = netOffline ? '网络连接错误，请重试' : error.message || '请求失败'
      AppMessage({
        type: 'error',
        message: msg,
        title: '',
        timer: errorModalAutoClose ? undefined : 0
      })
    }

    trackEvent.track('http_error', {
      http_url: url,
      http_message: error.message
    })
    throw error
  }

  async request<T = any>(url: string, config: RequestOptions): Promise<BaseResponse<T>> {
    try {
      const processedConfig = await this.processConfig(config)
      const controller = config.controller ?? new AbortController()
      const baseUrl = processedConfig.baseUrl || this.baseUrl
      let reqUrl = baseUrl + url
      if (url.startsWith('http') || url.startsWith('https')) {
        reqUrl = url
      }
      const response = await fetch(reqUrl, {
        ...processedConfig,
        signal: controller.signal
      })
      if (!response.ok) {
        throw new HttpError(response.status, response.statusText)
      }
      const responseType = processedConfig.responseType || 'json'
      let data
      if (responseType === 'json') {
        data = (await response.json()) || {}
        if (data.code !== 200) {
          if (config.retry && config.retry > 0) {
            return this.request(url, { ...config, retry: config.retry - 1 })
          }
          throw new HttpError(data.code, data.msg)
        }
      } else if (responseType === 'blob') {
        data = {
          code: response.status,
          data: await response.blob(),
          msg: 'success'
        }
      }
      const result: BaseResponse<T> = data
      return this.processResponse(result)
    } catch (error: any) {
      return this.handleError(error, url, config.errorModalAutoClose, config.isShowErrorModal)
    }
  }

  async get<T = any>(
    url: string,
    params?: { [key: string]: any },
    options?: RequestOptions
  ): Promise<BaseResponse<T>> {
    if (params) {
      const filterParams = Object.fromEntries(
        Object.entries(params).filter(([_, value]) => value !== undefined && value !== null)
      )
      url = `${url}?${new URLSearchParams(filterParams).toString()}`
    }
    return this.request(url, { method: 'GET', ...options })
  }

  async post<T = any>(url: string, data?: any, options?: RequestOptions): Promise<BaseResponse<T>> {
    return this.request(url, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json', ...options?.headers },
      ...options
    })
  }

  async postForm<T = any>(
    url: string,
    params: { [key: string]: any },
    options?: RequestOptions
  ): Promise<BaseResponse<T>> {
    const formData = new FormData()
    Object.keys(params).forEach(key => {
      if (params[key]) {
        if (Array.isArray(params[key])) {
          params[key].forEach((item: any) => {
            formData.append(key, item)
          })
        } else {
          formData.append(key, params[key])
        }
      }
    })
    return this.request(url, {
      method: 'POST',
      body: formData,
      ...options
    })
  }

  async put<T = any>(url: string, data?: any, options?: RequestOptions): Promise<BaseResponse<T>> {
    return this.request(url, {
      method: 'PUT',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json', ...options?.headers },
      ...options
    })
  }

  async delete<T = any>(url: string, options?: RequestOptions): Promise<BaseResponse<T>> {
    return this.request(url, { method: 'DELETE', ...options })
  }
}
